<script setup>
import {
	ref,
	computed,
	onMounted,
} from 'vue'

import { useRoute } from 'vue-router'
import { mdiCheck, mdiCar, mdiDelete, mdiClose } from '@mdi/js'
import CameraInput from '@/components/inputs/CameraInput.vue'
import { InspectionService } from '@/services/inspection-service'
import { ImageService } from '@/services/image-service'
import { useInspectionStore } from '@/stores/inspection-store'
import { Log } from '@/modules/util/log'
import { vehicleImageUrl } from '@/modules/util/vehicle-image'
import { ImageResizeManager } from '@/modules/util/imageresizer'
import { Notify } from '@/modules/util/notify'
import { HTTP } from '@/services/http'

const inspectionService = new InspectionService
const imageService = new ImageService
const store = useInspectionStore()
const route = useRoute()

const props = defineProps(['field', 'modelValue'])
const emit = defineEmits(['update:modelValue'])

const show = ref(false)
const showOptions = ref([])
const bikeGradeValues = ref(null)
const repairKey = ref(null)

const emptyItem = {
	date: null,
	component: null,
	repair: null,
	notes: null,
	image: null,
	imagename: null,
}

const newItem = () => {
	const item = { ...emptyItem }
	item.date = new Date().toLocaleString('en-GB')
	return item
}

const item = ref(newItem())
const validItem = computed(() => 
	item.value.component 
	&& item.value.repair
)

const showDelete = ref(false)
const selectedDamage = ref(null)

const updateDamageItems = async () => {
	show.value = false

	const items = [...(props.modelValue || [])]

	if (!items.includes(item.value))
		items.push(item.value)

	emit('update:modelValue', items)
	store.setDamageItems(items)
	item.value = newItem()
}

const onImageTaken = async (value) => {
	try {
		const res = await imageService.uploadInspection(route.params.id, value.url)
		if (!res) {
			throw new Error('Image upload failed')
		}

		const blob = await ImageResizeManager.loadAndResizeImage(value.url, props.field.width || 100)
		const url = URL.createObjectURL(blob)
        
		item.value.image = url
		item.value.uid = res.uid
	} catch (e) {
		Notify.error('Unexpected error: ' + e.message)
		delete item.value.image
		return
	}
}

const showDeleteDialog = (damage) => {
	showDelete.value = true
	selectedDamage.value = damage
}

const deleteDamageItem = () => {
	const newVal = [...props.modelValue]
	const index = props.modelValue.findIndex(val => val === selectedDamage.value)

	if (index > -1) {
		const item = newVal.splice(index, 1)
		emit('update:modelValue', newVal)

		if (item.uid)
			HTTP.deleteCached(item.uid)
	}

	store.setDamageItems(newVal)

	showDelete.value = false
	selectedDamage.value = null
}

const clearItemFields = (...fields) => {
	document.activeElement.blur()
    
	fields.forEach(field => {
		delete item.value[field]
	})

	if (fields.includes('repair') && fields.length === 1) {
		const options = Object.keys(bikeGradeValues.value[item.value.component])

		if (options.length === 1) {
			repairKey.value = options[0]
			item.value.repair = bikeGradeValues.value[item.value.component][repairKey.value]
		} else {
			repairKey.value = null
		}
	}
}

const openDialog = (damage=null) => {
	item.value = damage || newItem()
	show.value = true           
}

const closeDialog = () => {
	item.value = newItem()
	show.value = false             
}

const listBikeGradeValues = async () => {
	const promise = inspectionService.bikeGradeValues().then(res => {
		if (!res.body?.status) {
			throw new Error(JSON.stringify(res.body))
		}

		const { gradevalues } = res.body

		store.setBikeGradeValues(gradevalues)
		bikeGradeValues.value = gradevalues

		return gradevalues
	}).catch(err => {
		console.error('listBikeGradeValues:', err.message)
		Log.warning('Could not refresh NAMA grade data. You may have poor connectivity')
	})

	const item = store.allBikeGradeValues
	return item ? item : await promise
}

onMounted(listBikeGradeValues)
</script>

<template>
<div>
	<div class="w-100 text-center">
		<v-btn
			class="w-100 my-4"
			color="primary"
			@click="() => openDialog()"
		>
			{{ field.label }}
		</v-btn>
	</div>

	<v-dialog 
		v-model="show"
		fullscreen
	>
		<v-card>
			<v-toolbar>
				<v-btn
					:icon="mdiClose"
					class="ms-auto"
					variant="plain"
					@click="closeDialog"
				/>
				<v-toolbar-title>{{ field.label }}</v-toolbar-title>
			</v-toolbar>
			
			<v-card-text>
				<CameraInput
					class="my-4 w-100"
					variant="outlined"
					:color="item.image || item.imagename ? 'success' : 'primary'"
					:append-icon="item.image || item.imagename ? mdiCheck : undefined"
					:disabled="item.image"
					@update="onImageTaken"
				>
					{{ item.image || item.imagename ? 'Picture Taken' : 'Take Picture' }}
				</CameraInput>
				<v-select
					v-if="bikeGradeValues"
					v-model="item.component"
					:items="Object.keys(bikeGradeValues)"
					label="Component"
					@update:model-value="clearItemFields('repair')"
				/>
				<v-select
					v-if="item.component"
					v-model="repairKey"
					:items="Object.keys(bikeGradeValues[item.component])"
					label="Repair"
					@update:model-value="k => item.repair = bikeGradeValues[item.component][k]"
				/>
				<v-textarea
					v-model="item.notes"
					label="Notes"
					outlined
					dense
					clearable
					template
				/>
				<v-btn
					:disabled="!validItem"
					class="w-100 my-4"
					color="success"
					@click="updateDamageItems"
				>
					Save
				</v-btn>
			</v-card-text>
		</v-card>
	</v-dialog>

	<v-list>
		<v-list-item
			v-for="(damage, index) in (modelValue || []).filter(val => val.zone === field.zone)"
			:key="index"
			@click="() => {
				const current = showOptions[index]
				showOptions = []
				showOptions[index] = !current
			}"
		>
			<template #prepend>
				<v-avatar
					v-if="damage.image"
					:image="damage.image"
				/>
				<v-avatar
					v-else-if="damage.imagename"
					:image="vehicleImageUrl(damage.imagename)"
				/>
				<v-avatar
					v-else
					color="grey-lighten-1"
				>
					<v-icon color="white">
						{{ mdiCar }}
					</v-icon>
				</v-avatar>
			</template>
			<v-list-item-subtitle>
				{{ damage.date }}
			</v-list-item-subtitle>
			<v-list-item-title>
				{{ damage.component }} - {{ damage.repair?.repair }} - {{ damage.repair?.cost }}
			</v-list-item-title>
			<template #append>
				<v-btn
					v-show="!!showOptions[index]"
					color="success"
					size="small"
					style="margin-right: 8px"
					@click="openDialog(damage)"
				>
					Edit
				</v-btn>
				<v-btn
					v-show="!!showOptions[index]"
					color="error"
					size="small"
					:append-icon="mdiDelete"
					@click="showDeleteDialog(damage)"
				>
					Delete
				</v-btn>
			</template>
		</v-list-item>
	</v-list>

	<v-dialog v-model="showDelete">
		<v-card title="Are you sure?">  
			<v-card-text>This action cannot be undone.</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn @click="showDelete = false; selectedDamage = null">
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					@click="deleteDamageItem"
				>
					OK
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</div>
</template>

<style lang="scss" scoped>
</style>